import React, { useContext, useEffect, useState, useMemo } from "react";
import "../account.css";
import "./accountCategories.css";
import { CombinedContext } from "../../../context/combinedContext";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import CATEGORY_OPTIONS from "../../../constants/categoryOptions";
import BannerMessage from "../../common/bannerMessage/bannerMessage";
import { ChevronDown, LayoutGrid, Tag } from "lucide-react";

const AccountCategories = () => {
	const { language, userData, setUserData } = useContext(CombinedContext);

	const accessToken = userData?.cognitoTokens?.AccessToken;
	const shop = userData?.shop;

	const [category, setCategory] = useState("");
	const [subCategory, setSubCategory] = useState("");
	const [saving, setSaving] = useState(false);
	const [banner, setBanner] = useState({ text: "", type: "info", exiting: false });

	const t = translations[language].account.categories;
	const labelOf = (option) => (language === "es" ? option.labelES : option.labelEN);

	/* ----------------------------
	   DERIVED CATEGORY OBJECT
	----------------------------- */
	const selectedCategory = useMemo(() => CATEGORY_OPTIONS.find((c) => c.value === category), [category]);

	const selectedSubCategory = useMemo(
		() => selectedCategory?.subCategories.find((s) => s.value === subCategory),
		[selectedCategory, subCategory]
	);

	/* Whatever is live on the shop right now — not the pending edit */
	const savedCategory = useMemo(() => CATEGORY_OPTIONS.find((c) => c.value === shop?.category), [shop?.category]);
	const savedSubCategory = useMemo(
		() => savedCategory?.subCategories.find((s) => s.value === shop?.subCategory),
		[savedCategory, shop?.subCategory]
	);

	/* ----------------------------
	   LOAD CURRENT VALUES
	----------------------------- */
	useEffect(() => {
		if (!shop) return;

		setCategory(shop.category || "");
		setSubCategory(shop.subCategory || "");
	}, [shop]);

	/* Auto-dismiss the banner */
	useEffect(() => {
		if (!banner.text) return;

		const exitTimer = setTimeout(() => setBanner((prev) => ({ ...prev, exiting: true })), 2500);
		const removeTimer = setTimeout(() => setBanner({ text: "", type: "info", exiting: false }), 2750);

		return () => {
			clearTimeout(exitTimer);
			clearTimeout(removeTimer);
		};
	}, [banner.text]);

	/* ----------------------------
	   SAVE HANDLER
	----------------------------- */
	const saveCategory = async () => {
		if (!category || !subCategory) return;
		try {
			setSaving(true);

			const resp = await companyBasedRequest.updateShopCategory(shop.shopId, category, subCategory, accessToken);

			// 🔑 update shop INSIDE userData
			setUserData((prev) => ({
				...prev,
				shop: resp.shop,
			}));

			setBanner({ text: t.saved, type: "success", exiting: false });
		} catch (err) {
			console.error("Failed to update category", err);
			setBanner({ text: t.error, type: "error", exiting: false });
		} finally {
			setSaving(false);
		}
	};

	const isDirty = category !== (shop?.category || "") || subCategory !== (shop?.subCategory || "");

	return (
		<div className="ac-page apc">
			<header className="ac-head">
				{/* <span className="ac-eyebrow">{translations[language].account.menu?.category || "Categories"}</span> */}
				<h1 className="ac-title">{t.title}</h1>
				{t.subtitle && <p className="ac-sub">{t.subtitle}</p>}
			</header>

			{/* ── WHAT'S LIVE RIGHT NOW ── */}
			{/* <section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<Tag size={16} />
					</span>
					<h3>{t.current}</h3>
				</div>

				<div className="ac-card-body">
					{savedCategory ? (
						<div className="apc-chips">
							<span className="apc-chip is-primary">{labelOf(savedCategory)}</span>
							{savedSubCategory && <span className="apc-chip">{labelOf(savedSubCategory)}</span>}
						</div>
					) : (
						<p className="apc-hint">{t.notSet}</p>
					)}
				</div>
			</section> */}

			{/* ── PICKERS ── */}
			<section className="ac-card">
				<div className="ac-card-head">
					<span className="ac-icon">
						<LayoutGrid size={16} />
					</span>
					<h3>{t.title}</h3>
				</div>

				<div className="ac-card-body">
					<div className="apc-fields">
						<div className="ac-field">
							<p className="ac-label">{t.category}</p>
							<span className="ac-select-wrap">
								<select
									className="ac-select"
									value={category}
									onChange={(e) => {
										setCategory(e.target.value);
										setSubCategory("");
									}}
								>
									<option value="" disabled>
										{t.select}
									</option>

									{CATEGORY_OPTIONS.map((cat) => (
										<option key={cat.value} value={cat.value}>
											{labelOf(cat)}
										</option>
									))}
								</select>
								<ChevronDown size={16} />
							</span>
						</div>

						{selectedCategory && (
							<div className="ac-field">
								<p className="ac-label">{t.subCategory}</p>
								<span className="ac-select-wrap">
									<select className="ac-select" value={subCategory} onChange={(e) => setSubCategory(e.target.value)}>
										<option value="" disabled>
											{t.select}
										</option>

										{selectedCategory.subCategories.map((sub) => (
											<option key={sub.value} value={sub.value}>
												{labelOf(sub)}
											</option>
										))}
									</select>
									<ChevronDown size={16} />
								</span>
							</div>
						)}
					</div>

					{selectedCategory && selectedSubCategory && isDirty && (
						<div className="apc-preview">
							<span className="apc-chip is-primary">{labelOf(selectedCategory)}</span>
							<span className="apc-chip">{labelOf(selectedSubCategory)}</span>
						</div>
					)}

					<button className="ac-btn apc-save" onClick={saveCategory} disabled={saving || !category || !subCategory || !isDirty}>
						{saving ? t.saving : t.save}
					</button>
				</div>
			</section>

			{banner.text && <BannerMessage text={banner.text} type={banner.type} exiting={banner.exiting} />}
		</div>
	);
};

export default AccountCategories;
